import { Component, Vue, Watch } from 'vue-property-decorator';
import { sellerConfig } from '@/helpers/Config';
import { configYml, environment } from '@/helpers/Environment';
import { imageData } from '@/helpers/ImageHelpers';
import { indexItemsById } from '@/helpers/IndexHelpers';
import { fetchCartMod } from '@/helpers/PromoCodes';
import { getSummary } from '@/helpers/StringHelpers';
import MobileFooterPortal from '@/components/cart/MobileFooterPortal.vue';
import ReserveMobileFooter from '@/components/cart/ReserveMobileFooter.vue';
import FloatingNavigateBack from '@/components/elements/FloatingNavigateBack.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import { openDeleteCartOrCheckoutDialog } from '@/components/membership/DeleteCartOrCheckoutDialog.vue';
import MembershipForm from '@/components/membership/MembershipForm.vue';
import { TixTime } from '@/TixTime/TixTime';
import { fetchEventDetails } from '@/api/Events';
import { getMembershipRules } from '@/api/Membership';
import { apiErrorMessageOrRethrow, getApiErrorEntity, logInTrackJS } from '@/errors/helpers';
import { openDescriptionModal } from '@/modals/descriptionModal';
import { addMembershipToCart } from '@/state/Membership';
import store from '@/store/store';
@Component({
    name: 'RedeemMembershipRoute',
    components: {
        MobileFooterPortal,
        ReserveMobileFooter,
        FloatingNavigateBack,
        MembershipForm,
        FormInput2,
    },
})
export default class default_1 extends Vue {
    // `event` is the membership event that has ticket groups matching the gift code.
    event = null;
    // TODO Allow the Redeem GoM event to have its own image.
    // `defaultMembershipEvent` is the portal's default membership event which we use for getting an image to display.
    defaultMembershipEvent = null;
    giftCode = null;
    giftCodeMethod = null;
    groups = null;
    rules = null;
    fetchingGiftCode = false;
    submitting = false;
    loading = true;
    autoRenew = false;
    selectedLevel = null;
    surveyAnswers = {};
    details = {};
    countsByTTID = {};
    noAvailableMemberships = false;
    // Plain text error messages.
    reserveError = null;
    codeError = null;
    contexts = new Set(['redeem-gift-of-membership']);
    formId = 'redeem-membership-form';
    t;
    beforeRouteEnter(to, from, next) {
        const eventID = sellerConfig('membership_event_id');
        if (eventID) {
            next();
        }
        else {
            return next({ name: 'membership' });
        }
    }
    created() {
        // TODO Only get membership rules relevant to this membership event.
        getMembershipRules().then((result) => {
            this.rules = result;
        });
        const giftCode = this.$route.query['gift_code'];
        if (giftCode) {
            if (giftCode.length === 10) {
                this.giftCode = giftCode;
                this.fetchGiftCode().then(() => {
                    if (!this.codeError) {
                        this.giftCodeMethod = 'query';
                    }
                });
            }
            else {
                this.codeError = 'You must enter a valid code';
            }
        }
        // TODO Show a image specific to Redeem Membership.
        // Only for showing the event image.
        const eventID = sellerConfig('membership_event_id');
        if (eventID) {
            fetchEventDetails(eventID)
                .then((event) => {
                this.defaultMembershipEvent = event;
                this.loading = false;
            })
                .catch((e) => {
                if (e.notFoundError) {
                    logInTrackJS('Membership event could not be loaded', { eventID, slug: environment.seller?.slug });
                }
                else {
                    apiErrorMessageOrRethrow(e);
                }
            })
                .finally(() => (this.loading = false));
        }
        else {
            this.loading = false;
        }
        const membership = store.getters['Cart/cartItems'].find((item) => item.isMembershipEvent);
        if (membership) {
            openDeleteCartOrCheckoutDialog({
                title: 'Redeem Membership',
                description: 'You need to take action on your current cart before you can redeem a membership.',
            });
        }
    }
    submitGiftCode() {
        this.fetchGiftCode().then(() => {
            if (!this.codeError) {
                this.giftCodeMethod = 'input';
            }
        });
    }
    fetchGiftCode() {
        this.fetchingGiftCode = true;
        return fetchCartMod(this.giftCode, { includeCodeLookup: true })
            .then(this.handleGiftCodeResponse)
            .catch((error) => {
            const apiError = getApiErrorEntity(error);
            if (apiError?._code === 'not_found') {
                this.codeError = 'You must enter a valid code';
            }
            else {
                this.codeError = apiErrorMessageOrRethrow(error);
            }
        })
            .finally(() => (this.fetchingGiftCode = false));
    }
    handleGiftCodeResponse(response) {
        const ticketTypes = response.ticket_type._data;
        if (this.isExpired(response.code_lookup)) {
            this.codeError = 'This code has expired';
        }
        else if (this.isExhausted(response.code_lookup)) {
            this.codeError = 'This code has already been redeemed';
        }
        else {
            const membershipTTs = ticketTypes.filter((type) => type.event_template_category === 'Membership');
            this.fetchEvent(membershipTTs).then((event) => {
                if (event) {
                    this.event = event;
                    this.setDiscountedTicketGroups(event, membershipTTs);
                    this.groups = (this.groups ?? []).map((group) => {
                        return {
                            ...group,
                            summary: group.meta.redeem_gom_summary ? group.meta.redeem_gom_summary : group.summary,
                            description: group.meta.redeem_gom_description ? group.meta.redeem_gom_description : group.description,
                        };
                    });
                }
            });
        }
    }
    isExpired(response) {
        const codeLookup = response._data[0];
        const currentTime = new TixTime();
        const beforeStart = codeLookup.valid_from && !currentTime.isAfter(codeLookup.valid_from);
        const afterEnd = codeLookup.valid_to && !currentTime.isBefore(codeLookup.valid_to);
        return beforeStart || afterEnd;
    }
    isExhausted(response) {
        const codeLookup = response._data[0];
        return codeLookup.exhausted;
    }
    fetchEvent(ticketTypes) {
        const eventIds = new Set(ticketTypes.map((type) => type.event_template_id));
        if (eventIds.size === 1) {
            const [eventId] = eventIds.values();
            return fetchEventDetails(eventId);
        }
        else if (eventIds.size > 1 && window.tix.web?.membership_event_id) {
            return fetchEventDetails(window.tix.web.membership_event_id);
        }
        else {
            this.noAvailableMemberships = true;
            return Promise.resolve();
        }
    }
    setDiscountedTicketGroups(event, membershipTicketTypes) {
        const ticketTypesById = indexItemsById(membershipTicketTypes);
        this.groups = event.ticketGroups
            .filter((group) => ticketTypesById[group.types[0].id])
            .sort((a, b) => {
            const aType = ticketTypesById[a.types[0].id];
            const bType = ticketTypesById[b.types[0].id];
            if (aType.discounted_amount === bType.discounted_amount) {
                return Number(bType.currency_amount) - Number(aType.currency_amount);
            }
            else {
                return Number(aType.discounted_amount) - Number(bType.discounted_amount);
            }
        })
            // Only show groups of equal or higher rank to the main gifted membership.
            .filter((group, _, groups) => group._rank >= groups[0]._rank)
            // Only show the gifted membership and 2 upgrade options.
            .slice(0, 3)
            .map((group) => {
            return {
                ...group,
                price: Number(ticketTypesById[group.types[0].id].discounted_amount),
                originalPrice: Number(ticketTypesById[group.types[0].id].currency_amount),
            };
        });
        this.selectedLevel = this.groups[0].id;
    }
    // autoRenew and extendedMembershipOption are ignored
    // We don't support auto-renewal or extended memberships on the redeem membership route
    onSubmit({ tickets }) {
        return Promise.resolve()
            .then(() => {
            return addMembershipToCart(this.event, this.rules, this.selectedLevel, [this.giftCode], tickets, this.surveyAnswers, this.autoRenew);
        })
            .then(() => {
            this.$store.commit('Cart/redeemMembership', true);
            this.$router.push(this.nextRoute);
        })
            .catch((error) => {
            if (error.validationError) {
                // Ignore validation errors and leave the promise resolved.
            }
            else {
                // API error messages do not include HTML.
                this.reserveError = apiErrorMessageOrRethrow(error, 'rule_blocks_reserve:checkout_rules');
            }
        })
            .finally(() => {
            this.submitting = false;
        });
    }
    get nextRoute() {
        return environment.web.upsells ? '/events' : '/checkout';
    }
    get image() {
        if (this.defaultMembershipEvent) {
            const result = imageData(this.defaultMembershipEvent, 'cover');
            if (result.url)
                return result;
        }
        return undefined;
    }
    get titleInsideContentColumn() {
        return configYml.titleInsideContentColumn;
    }
    get title() {
        return this.languageStrings.title;
    }
    get description() {
        return this.languageStrings.description;
    }
    get summary() {
        return getSummary(this.description) ?? '';
    }
    get languageStrings() {
        return this.giftCodeMethod === 'query' ? this.t.queryParam : this.t.input;
    }
    get reserveButtonLabels() {
        const language = this.$t('payButton');
        return {
            desktop: language.desktop.proceedButtonLabel,
            mobile: language.mobile.proceedButtonLabel,
        };
    }
    @Watch('giftCode')
    onGiftCodeChange() {
        this.codeError = null;
    }
    openDescriptionModal() {
        openDescriptionModal(this.autoP(this.description), { type: 'membership' });
    }
}
